<script lang="ts" setup>
import { computed, resolveComponent } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import useButton from './useButton'
import { ButtonSize, ButtonVariant } from './types'

interface Props {
  variant?: keyof typeof ButtonVariant
  size?: keyof typeof ButtonSize
  to?: RouteLocationRaw
  disabled?: boolean
  dense?: boolean
  as?: string
  type?: string
}

const props = withDefaults(defineProps<Props>(), {
  variant: ButtonVariant.default,
  size: ButtonSize.md,
  disabled: false,
  dense: false,
  type: 'button',
})

const emit = defineEmits<{ (e: 'click', event: MouseEvent): void }>()

const { tag, bound, variantClass } = useButton({
  to: computed(() => props.to),
  disabled: computed(() => props.disabled),
  variant: computed(() => props.variant),
})

const computedComponent = computed(() => {
  if (props.as)
    return props.as

  if (tag.value === 'nuxt-link')
    return resolveComponent('nuxt-link')

  return tag.value
})

const sizeClass = computed((): string[] => {
  let cls = {}
  switch (props.size) {
    case ButtonSize.xs:
      cls = { padding: 'px-2 py-2', text: 'text-xs' }
      break
    case ButtonSize.sm:
      cls = { padding: 'px-3 py-2', text: 'text-sm' }
      break
    case ButtonSize.md:
      cls = { padding: 'px-6 py-3', text: 'text-base' }
      break
    case ButtonSize.lg:
      cls = { padding: 'px-8 py-3.5', text: 'text-lg' }
      break
  }
  if (props.dense)
    cls = { ...cls, padding: 'p-0' }

  return Object.values(cls)
})

const buttonClass = computed((): string[] => {
  const base = [
    'inline-flex',
    'flex-grow-0',
    'cursor-pointer',
    'items-center',
    'justify-center',
    'gap-2',
    'rounded-md',
    'border-0',
    'font-medium',
  ]

  return [...base, ...variantClass.value, ...sizeClass.value]
})

const onClick = (e: MouseEvent) => {
  emit('click', e)
}
</script>

<template>
  <component
    :is="computedComponent"
    :class="buttonClass"
    :type="type"
    v-bind="bound"
    @click="onClick"
  >
    <slot />
  </component>
</template>
